import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { useOptionalSidebar } from '../ui/sidebar';
import { Users, Building2, Landmark, CalendarClock, BookOpen } from 'lucide-react';
import './AdminTabs.css';

/**
 * AdminTabs
 * Pill-style tab bar for moving between admin sections in the MOBILE shell.
 * On desktop the app sidebar's Admin group lists the same pages, so this
 * renders nothing inside the desktop SidebarProvider. Tabs that are gated by
 * feature flags only appear when their flag is enabled.
 */
const AdminTabs = () => {
  // The phone New look mounts a provider too, but its sidebar is a sheet, so
  // the tabs still render there.
  const sidebar = useOptionalSidebar();
  const inDesktopShell = Boolean(sidebar) && !sidebar.isMobile;
  const location = useLocation();
  const adminOpsEnabled = useFeatureFlagEnabled('admin-ops');
  const nightAuditConfigEnabled = useFeatureFlagEnabled('night-audit-config');

  const tabs = [
    {
      label: 'Users',
      to: '/admin/users',
      icon: Users,
      active: location.pathname === '/admin/users' || location.pathname === '/admin'
    },
    ...(adminOpsEnabled !== false
      ? [{
          label: 'Organizations',
          to: '/admin/organizations',
          icon: Building2,
          active: location.pathname.startsWith('/admin/organizations')
        }]
      : []),
    {
      label: 'Comp Sets',
      to: '/admin/comp-sets',
      icon: Landmark,
      active: location.pathname.startsWith('/admin/comp-sets')
    },
    {
      label: 'AI Skills',
      to: '/admin/ai-skills',
      icon: BookOpen,
      active: location.pathname.startsWith('/admin/ai-skills')
    },
    ...(nightAuditConfigEnabled
      ? [{
          label: 'Night Audit',
          to: '/admin/night-audit-config',
          icon: CalendarClock,
          active: location.pathname.startsWith('/admin/night-audit-config')
        }]
      : []),
  ];

  if (inDesktopShell || tabs.length <= 1) return null;

  return (
    <nav className="adm-tabs" aria-label="Admin sections">
      <div className="adm-tabs-strip">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <Link
              key={tab.to}
              to={tab.to}
              className={`adm-tab${tab.active ? ' adm-tab-on' : ''}`}
              aria-current={tab.active ? 'page' : undefined}
            >
              <Icon size={15} aria-hidden="true" />
              {tab.label}
            </Link>
          );
        })}
      </div>
    </nav>
  );
};

export default AdminTabs;
